// "Passengers" section of a ShareJet booking: passengers grouped by seat-holder, with add/edit/delete,
// cancel & release per user, cancelled users, and adding a customer with N seats.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button, Card, Form, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';

import {
  addAdminPassenger,
  addUserToBooking,
  cancelBookingUser,
  deleteAdminPassenger,
  updateAdminPassenger,
  uploadFile,
} from '../../api/endpoints';
import type { AdminBookingDetail, AdminBookingPassenger } from '../../api/types';
import type { BookingPassengerAddBody, BookingPassengerUpdateBody } from '../../api/schemas';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';
import { passengerTableColumns } from './passengerTableColumns';
import { CancelledUsersCard } from './CancelledUsersCard';
import { PassengerModal } from './PassengerModal';
import { AddUserModal } from './AddUserModal';
import { AccountDeletedTag } from '../../components/AccountDeletedTag';

const { Text } = Typography;

/** The tooltip BookingDetailPage's disabled section editors give (SHAR-591). */
export const CANCELLED_LOCKED = 'This booking is cancelled — its details can no longer be changed';

// SHAR-48 — passengers grouped by seat-holder, with per-user add/edit/delete
// and passport/document viewing + upload. Admin may edit any passenger.
export function PassengersSection({
  booking,
  bookingId,
}: {
  booking: AdminBookingDetail;
  bookingId: string;
}) {
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminBookingPassenger | null>(null);
  const [ownerId, setOwnerId] = useState<string>('');
  const [passportUrl, setPassportUrl] = useState<string | null>(null);
  const [extraUrls, setExtraUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['booking', bookingId] });

  const addMut = useMutation({
    mutationFn: (body: BookingPassengerAddBody) => addAdminPassenger(bookingId, body),
    onSuccess: () => {
      message.success('Passenger added');
      setModalOpen(false);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Add failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ pid, body }: { pid: string; body: BookingPassengerUpdateBody }) =>
      updateAdminPassenger(bookingId, pid, body),
    onSuccess: () => {
      message.success('Passenger updated');
      setModalOpen(false);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });
  const deleteMut = useMutation({
    mutationFn: (pid: string) => deleteAdminPassenger(bookingId, pid),
    onSuccess: () => {
      message.success('Passenger removed');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Delete failed'),
  });
  // SHAR-106 — cancel a specific user's participation and release their pre-auth.
  const cancelUserMut = useMutation({
    mutationFn: (userId: string) => cancelBookingUser(bookingId, userId),
    onSuccess: () => {
      message.success('User cancelled and pre-authorisation released');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Cancel failed'),
  });

  // SHAR-609 — documents are staged for deletion rather than removed on the
  // spot: the set of urls marked to go, applied on Save and thrown away on
  // Cancel. An admin who clicks the wrong bin on a passport should be able to
  // back out, and before this there was no delete at all.
  const [stagedDeletes, setStagedDeletes] = useState<string[]>([]);
  const stageDelete = (url: string) =>
    setStagedDeletes((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));

  const openAdd = (owner: string) => {
    setEditing(null);
    setOwnerId(owner);
    setPassportUrl(null);
    setExtraUrls([]);
    setStagedDeletes([]);
    form.resetFields();
    setModalOpen(true);
  };
  const openEdit = (p: AdminBookingPassenger) => {
    setEditing(p);
    setOwnerId(p.ownerUserId);
    setPassportUrl(p.passportPhotoUrl);
    setExtraUrls(p.extraDocUrls ?? []);
    setStagedDeletes([]);
    form.setFieldsValue({
      fullName: p.fullName,
      dateOfBirth: p.dateOfBirth ? dayjs(p.dateOfBirth) : null,
      gender: p.gender ?? undefined,
      nationality: p.nationality,
      passportPending: p.passportPending,
      passportNumber: p.passportNumber ?? '',
      issuingCountry: p.issuingCountry ?? '',
      passportExpiry: p.passportExpiry ? dayjs(p.passportExpiry) : null,
    });
    setModalOpen(true);
  };

  const doUpload = async (file: File, kind: 'passport' | 'extra') => {
    try {
      setUploading(true);
      const { url } = await uploadFile(file, 'passenger-doc');
      if (kind === 'passport') setPassportUrl(url);
      else setExtraUrls((u) => [...u, url]);
    } catch {
      message.error('Upload failed');
    } finally {
      setUploading(false);
    }
    return false;
  };

  // Regression 28 Sep — one submit at a time.
  const submitGuard = useSingleSubmit();
  const submit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    // Regression 28 Sep — the admin form has no "to be provided" checkbox
    // (SHAR-448), so `v.passportPending` never exists and every save sent
    // passportPending:false with null passport fields: editing a "To be
    // provided" passenger (even just the name) failed 400 "Invalid date: null".
    // A passenger who is pending STAYS pending while the passport fields are
    // left empty; filling them in clears it.
    const noPassport = !v.passportNumber && !v.issuingCountry && !v.passportExpiry;
    const defer = noPassport && !!editing?.passportPending;
    const body: BookingPassengerAddBody = {
      ownerUserId: ownerId || undefined,
      fullName: v.fullName,
      dateOfBirth: v.dateOfBirth ? (v.dateOfBirth as Dayjs).format('YYYY-MM-DD') : '',
      gender: v.gender,
      nationality: v.nationality,
      passportPending: defer,
      passportNumber: defer ? null : v.passportNumber || null,
      issuingCountry: defer ? null : v.issuingCountry || null,
      passportExpiry: defer
        ? null
        : v.passportExpiry
        ? (v.passportExpiry as Dayjs).format('YYYY-MM-DD')
        : null,
      // SHAR-609 — the staged deletions are applied HERE, on save, and nowhere
      // else. Cancelling the modal simply drops the staged set, so the documents
      // are untouched, which is the whole point of staging them.
      passportPhotoUrl: passportUrl && stagedDeletes.includes(passportUrl) ? null : passportUrl,
      extraDocUrls: extraUrls.filter((u) => !stagedDeletes.includes(u)),
    };
    if (editing) await updateMut.mutateAsync({ pid: editing.id, body });
    else await addMut.mutateAsync(body);
  });

  const passengersFor = (uid: string) => booking.passengers.filter((p) => p.ownerUserId === uid);
  // SHAR-440 — a user's booked seat count (from the per-user payment breakdown),
  // so the "Add passenger" button is disabled once passengers == booked seats.
  //
  // Regression 28 Sep — the limit is the seats the holder HOLDS (the API's
  // participant.seats, the number adminAddPassenger enforces). It used to be
  // the PAID seats, which are 0 after a hold lapses, and 0 meant "no limit":
  // the button stayed enabled on a full group and the API then refused.
  const seatsForUser = (uid: string) =>
    booking.participants.find((p) => p.id === uid)?.seats ??
    booking.userPayments?.find((p) => p.userId === uid)?.seats ??
    0;
  // Admin regression 6 Oct — a cancelled booking is final (SHAR-591): its
  // passenger and per-user actions are disabled like its section editors,
  // with the same tooltip, and the API refuses them too.
  const cancelled = booking.status === 'cancelled';
  const lockedReason = cancelled ? CANCELLED_LOCKED : undefined;
  const canAddPassenger = (uid: string) => !cancelled && passengersFor(uid).length < seatsForUser(uid);

  // SHAR-108 — add an existing customer to this booking with N seats. Not on a
  // cancelled or completed booking (PO, 30 Sep).
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm] = Form.useForm<{ userId: string; seats: number }>();
  const seatsLeft = Math.max(0, booking.seatsTotal - booking.seatsBooked);
  const canAddUser = booking.status !== 'cancelled' && booking.status !== 'complete' && seatsLeft > 0;
  // PO, 30 Sep — the seats are VALIDATED, never silently clamped, against the
  // seats free right now: re-read when the modal opens; on submit the server
  // decides and its message is shown on the field.
  const [refreshingSeats, setRefreshingSeats] = useState(false);
  const watchedUserId = Form.useWatch('userId', addUserForm);
  const watchedSeats = Form.useWatch('seats', addUserForm);
  const seatsError = (v: unknown): string | null => {
    if (seatsLeft < 1) return 'No seats are free on this booking now';
    const range = `Enter 1–${seatsLeft} ${seatsLeft === 1 ? 'seat' : 'seats'} (${seatsLeft} free now)`;
    if (v === null || v === undefined || v === '') return range;
    const n = Number(v);
    if (!Number.isInteger(n) || n < 1 || n > seatsLeft) return range;
    return null;
  };
  const addUserValid = !!watchedUserId && seatsError(watchedSeats) === null && !refreshingSeats;
  const openAddUser = async () => {
    setAddUserOpen(true);
    setRefreshingSeats(true);
    try {
      await qc.refetchQueries({ queryKey: ['booking', bookingId] });
    } finally {
      setRefreshingSeats(false);
    }
  };
  // The free seats can change while the modal is open (a refetch, or the
  // server's answer): re-check a value already entered against the new number.
  useEffect(() => {
    if (addUserOpen && addUserForm.isFieldTouched('seats')) {
      addUserForm.validateFields(['seats']).catch(() => undefined);
    }
  }, [seatsLeft, addUserOpen, addUserForm]);
  const addUserMut = useMutation({
    mutationFn: (v: { userId: string; seats: number }) => addUserToBooking(bookingId, v.userId, v.seats),
    onSuccess: (res) => {
      message.success(
        res.alreadyOnBooking
          ? `This user was already on the booking: ${res.addedSeats} ${res.addedSeats === 1 ? 'seat was' : 'seats were'} added to their seats. They have been asked to pay.`
          : `User added with ${res.addedSeats} ${res.addedSeats === 1 ? 'seat' : 'seats'}. They have been asked to pay.`,
        6,
      );
      setAddUserOpen(false);
      addUserForm.resetFields();
      invalidate();
    },
    onError: (e: any) => {
      const msg: string = e?.response?.data?.message || 'Could not add the user';
      // The seats changed meanwhile (someone booked): the server's figure wins.
      // Show it on the field and re-read the booking so the limit updates.
      if (/seat/i.test(msg)) {
        addUserForm.setFields([{ name: 'seats', errors: [msg] }]);
        qc.invalidateQueries({ queryKey: ['booking', bookingId] });
      } else {
        message.error(msg);
      }
    },
  });

  return (
    <>
      <Card
        title="Passengers"
        extra={
          <Tooltip
            title={
              // The status is the reason first: a completed or cancelled booking
              // that also happens to be full is closed, not merely sold out.
              canAddUser
                ? undefined
                : booking.status === 'cancelled' || booking.status === 'complete'
                  ? 'Users cannot be added to a cancelled or completed booking'
                  : 'No seats are left on this booking'
            }
          >
            <Button size="small" icon={<PlusOutlined />} disabled={!canAddUser} onClick={openAddUser}>
              Add user
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {booking.participants.map((part) => (
            <Card
              key={part.id}
              type="inner"
              size="small"
              title={
                // Admin regression 6 Oct — a long name is cut with an ellipsis
                // and shown whole in a tooltip; the row shrinks (minWidth 0)
                // instead of running under the buttons on the right.
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, paddingInlineEnd: 8 }}>
                  <Text strong ellipsis={{ tooltip: part.name || undefined }} style={{ minWidth: 0 }}>
                    {part.name || '—'}
                  </Text>
                  {part.isCreator ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>Creator</Tag> : null}
                  <AccountDeletedTag at={part.accountDeletedAt} />
                </div>
              }
              extra={
                <Space>
                  {/* SHAR-440 — disable once the user's passengers reach their booked seats. */}
                  <Tooltip
                    title={
                      canAddPassenger(part.id)
                        ? ''
                        : cancelled
                          ? CANCELLED_LOCKED
                          : seatsForUser(part.id) > 0
                          ? `This user has passengers for all ${seatsForUser(part.id)} of their booked seats`
                          : 'This user holds no seats on this booking'
                    }
                  >
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      disabled={!canAddPassenger(part.id)}
                      onClick={() => openAdd(part.id)}
                    >
                      Add passenger
                    </Button>
                  </Tooltip>
                  {/* SHAR-106 — cancel this user's seats + release their pre-auth. */}
                  <Popconfirm
                    title="Cancel this user's booking?"
                    description="Their seats are freed and any pre-authorisation hold is released."
                    okText="Cancel & Release"
                    okButtonProps={{ danger: true, loading: cancelUserMut.isPending }}
                    disabled={cancelled}
                    onConfirm={() => cancelUserMut.mutate(part.id)}
                  >
                    <Tooltip title={lockedReason ?? ''}>
                      <Button size="small" danger disabled={cancelled}>
                        Cancel &amp; Release
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              }
            >
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {part.email || '—'}
                {part.phone ? ` · ${part.phone}` : ''}
              </Text>
              <Table<AdminBookingPassenger>
                rowKey="id"
                dataSource={passengersFor(part.id)}
                pagination={false}
                size="small"
                // Regression 29 Sep - a long unbroken value (an email, a passport
                // number) made this table wider than its card and the whole page
                // scrolled sideways; it now scrolls inside the card instead.
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No passengers' }}
                columns={passengerTableColumns({ openEdit, deleteMut, lockedReason })}
              />
            </Card>
          ))}

          {/* SHAR-466 — users who cancelled (or were cancelled via Cancel & Release)
              are kept here so the admin retains a record; they are removed from the
              Revenue tab but must not disappear from the booking entirely. */}
          {booking.cancelledUsers && booking.cancelledUsers.length > 0 ? (
            <CancelledUsersCard booking={booking} />
          ) : null}
        </Space>
      </Card>

      <PassengerModal
        form={form}
        editing={editing}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        submit={submit}
        submitGuard={submitGuard}
        addMut={addMut}
        updateMut={updateMut}
        uploading={uploading}
        doUpload={doUpload}
        passportUrl={passportUrl}
        extraUrls={extraUrls}
        stagedDeletes={stagedDeletes}
        stageDelete={stageDelete}
      />
      {/* SHAR-108 — add an existing customer with N seats (at most the seats left). */}
      <AddUserModal
        addUserForm={addUserForm}
        addUserOpen={addUserOpen}
        setAddUserOpen={setAddUserOpen}
        onOk={() => addUserForm.validateFields().then((v) => addUserMut.mutate(v))}
        addUserValid={addUserValid}
        addUserMut={addUserMut}
        refreshingSeats={refreshingSeats}
        seatsLeft={seatsLeft}
        seatsError={seatsError}
      />
    </>
  );
}
